import { ColorUtils } from '../utils/ColorUtils.js';
import { NotificationManager } from '../utils/notifications.js';

/**
 * Theme Manager - Handles dynamic theming from backend configuration
 * Generates full color palette, validates contrast, and applies to DOM
 */
export class ThemeManager {
  constructor() {
    this.defaultPalette = {
      primary: '#3E885B',
      secondary: '#F9C846',
      accent: '#C7D941'
    };
    
    this.currentTheme = null;
    this.themeChangeListeners = [];
    
    // WCAG contrast requirements
    this.contrastRequirements = {
      AA_normal: 4.5,
      AA_large: 3,
      AAA_normal: 7,
      AAA_large: 4.5
    };
  }
  
  /**
   * Load theme from device branding configuration
   * @param {Object} branding - Device branding object
   * @returns {Object} Generated theme
   */
  loadFromDeviceBranding(branding) {
    try {
      console.log('Loading theme from device branding:', branding);
      
      // Extract colors from device branding
      const baseColors = {
        primary: branding.primaryColor || this.defaultPalette.primary,
        secondary: branding.secondaryColor || this.defaultPalette.secondary,
        accent: branding.accentColor || this.defaultPalette.accent
      };
      
      // Generate and apply theme
      const theme = this.generateFullTheme(baseColors);
      this.applyThemeToDOM(theme);
      
      // Apply additional device-specific styles
      this.applyDeviceStyles(branding);
      
      this.currentTheme = theme;
      this.notifyThemeChange(theme);
      
      console.log('Device branding theme applied successfully');
      return theme;
    } catch (error) {
      console.error('Failed to load device branding theme:', error);
      // Fallback to default theme
      return this.loadFromBackend({});
    }
  }
  
  /**
   * Load theme from backend configuration
   * @param {Object} config - Backend configuration object
   * @returns {Object} Generated theme
   */
  loadFromBackend(config) {
    try {
      // Extract colors and typography from backend or use defaults
      const baseColors = this.extractBaseColors(config);
      const typography = this.extractTypography(config);
      
      // Validate colors
      const validation = this.validateColors(baseColors);
      if (validation.errors.length > 0) {
        console.error('Theme validation errors:', validation.errors);
        NotificationManager.warning('Theme colors adjusted for accessibility');
      }
      
      // Apply corrections if needed
      const correctedColors = this.applyCorrections(baseColors, validation.suggestions);
      
      // Generate full theme with typography
      const theme = this.generateFullTheme(correctedColors, typography);
      
      // Apply to DOM
      this.applyTheme(theme);
      
      // Store for offline use
      this.cacheTheme(theme);
      
      this.currentTheme = theme;
      this.notifyListeners(theme);
      
      return theme;
    } catch (error) {
      console.error('Failed to load theme:', error);
      NotificationManager.error('Failed to apply theme, using defaults');
      
      // Fallback to default theme
      const fallbackTheme = this.generateFullTheme(this.defaultPalette);
      this.applyTheme(fallbackTheme);
      return fallbackTheme;
    }
  }
  
  /**
   * Extract base colors from backend config
   * @param {Object} config - Backend configuration
   * @returns {Object} Base colors
   */
  extractBaseColors(config) {
    const colorPalette = config?.kioskDisplaySettings?.colorPalette || {};
    
    // Return the full palette with all semantic colors
    return {
      primary: colorPalette.primary || config?.branding?.primaryColor || this.defaultPalette.primary,
      secondary: colorPalette.secondary || config?.branding?.secondaryColor || this.defaultPalette.secondary,
      accent: colorPalette.accent || config?.branding?.accent || this.defaultPalette.accent,
      // Additional semantic colors from backend
      error: colorPalette.error || '#F44336',
      warning: colorPalette.warning || '#FF9800',
      success: colorPalette.success || '#4CAF50',
      info: colorPalette.info || '#2196F3',
      surface: colorPalette.surface || '#F5F5F5',
      background: colorPalette.background || '#FFFFFF',
      // Text colors on backgrounds
      onPrimary: colorPalette.onPrimary || '#FFFFFF',
      onSecondary: colorPalette.onSecondary || '#FFFFFF',
      onSurface: colorPalette.onSurface || '#000000',
      onBackground: colorPalette.onBackground || '#000000',
      onError: colorPalette.onError || '#FFFFFF'
    };
  }
  
  /**
   * Extract typography configuration from backend config
   * @param {Object} config - Backend configuration
   * @returns {Object} Typography configuration
   */
  extractTypography(config) {
    const typography = config?.kioskDisplaySettings?.typography || {};
    const branding = config?.branding || {};
    
    return {
      // Primary font family (for body text)
      fontFamily: typography.fontFamily || branding.fontFamily || 'Montserrat, sans-serif',
      // Heading font family (for titles and headers)
      headingFont: typography.headingFont || typography.fontFamily || branding.fontFamily || 'Lora, serif',
      // Body font family (for regular text)
      bodyFont: typography.bodyFont || typography.fontFamily || branding.fontFamily || 'Montserrat, sans-serif',
      // Alternative fonts
      accentFont: typography.accentFont || typography.headingFont || 'Lora, serif',
      monoFont: typography.monoFont || '"Fira Code", "Consolas", monospace',
      // Font weights
      fontWeights: {
        light: typography.fontWeightLight || '300',
        normal: typography.fontWeightNormal || '400',
        medium: typography.fontWeightMedium || '500',
        semibold: typography.fontWeightSemibold || '600',
        bold: typography.fontWeightBold || '700'
      },
      // Font sizes (in rem units)
      fontSizes: {
        xs: typography.fontSizeXs || '0.75rem',
        sm: typography.fontSizeSm || '0.875rem',
        base: typography.fontSizeBase || '1rem',
        lg: typography.fontSizeLg || '1.125rem',
        xl: typography.fontSizeXl || '1.25rem',
        '2xl': typography.fontSize2xl || '1.5rem',
        '3xl': typography.fontSize3xl || '1.875rem',
        '4xl': typography.fontSize4xl || '2.25rem'
      }
    };
  }
  
  /**
   * Validate colors for format and contrast
   * @param {Object} colors - Base colors to validate
   * @returns {Object} Validation result
   */
  validateColors(colors) {
    const validation = {
      valid: true,
      errors: [],
      warnings: [],
      suggestions: {}
    };
    
    // Validate color formats
    Object.entries(colors).forEach(([name, color]) => {
      if (!ColorUtils.isValidColor(color)) {
        validation.errors.push(`Invalid ${name} color format: ${color}`);
        validation.valid = false;
        validation.suggestions[name] = this.defaultPalette[name];
      }
    });
    
    // Check contrast ratios for key combinations
    const contrastChecks = [
      {
        fg: colors.primary,
        bg: '#FFFFFF',
        min: this.contrastRequirements.AA_large,
        name: 'Primary on white',
        key: 'primary'
      },
      {
        fg: '#FFFFFF',
        bg: colors.primary,
        min: this.contrastRequirements.AA_normal,
        name: 'White on primary',
        key: 'textOnPrimary'
      },
      {
        fg: colors.secondary,
        bg: '#FFFFFF',
        min: this.contrastRequirements.AA_large,
        name: 'Secondary on white',
        key: 'secondary'
      }
    ];
    
    contrastChecks.forEach(check => {
      const ratio = ColorUtils.getContrastRatio(check.fg, check.bg);
      
      if (ratio < check.min) {
        validation.warnings.push(
          `${check.name}: Contrast ${ratio.toFixed(2)} below WCAG AA (${check.min})`
        );
        
        // Suggest adjusted color
        if (check.key === 'textOnPrimary') {
          // For text on colored background, choose white or dark
          validation.suggestions[check.key] = ColorUtils.getContrastingText(check.bg);
        } else {
          // Adjust the color itself
          const adjusted = ColorUtils.adjustForContrast(check.fg, check.bg, check.min);
          validation.suggestions[check.key] = adjusted;
        }
      }
    });
    
    return validation;
  }
  
  /**
   * Apply suggested corrections to colors
   * @param {Object} colors - Original colors
   * @param {Object} suggestions - Suggested corrections
   * @returns {Object} Corrected colors
   */
  applyCorrections(colors, suggestions) {
    return {
      ...colors,
      ...suggestions
    };
  }
  
  /**
   * Generate full theme from base colors and typography
   * @param {Object} baseColors - Base colors (primary, secondary, accent)
   * @param {Object} typography - Typography configuration (optional)
   * @returns {Object} Full theme object
   */
  generateFullTheme(baseColors, typography = null) {
    const theme = {
      // Base colors
      colors: {
        primary: baseColors.primary,
        secondary: baseColors.secondary,
        accent: baseColors.accent,
        
        // Semantic colors from backend or defaults
        success: baseColors.success || '#10B981',
        warning: baseColors.warning || '#F59E0B',
        error: baseColors.error || '#EF4444',
        info: baseColors.info || '#0EA5E9',
        surface: baseColors.surface || '#F5F5F5',
        background: baseColors.background || '#FFFFFF'
      },
      
      // Generate scales
      scales: {
        primary: ColorUtils.generateColorScale(baseColors.primary),
        secondary: ColorUtils.generateColorScale(baseColors.secondary),
        accent: ColorUtils.generateColorScale(baseColors.accent),
        neutral: ColorUtils.generateNeutralScale(baseColors.primary)
      },
      
      // Generate variants
      variants: {
        primaryLight: ColorUtils.lighten(baseColors.primary, 20),
        primaryDark: ColorUtils.darken(baseColors.primary, 20),
        secondaryLight: ColorUtils.lighten(baseColors.secondary, 20),
        secondaryDark: ColorUtils.darken(baseColors.secondary, 20),
        accentLight: ColorUtils.lighten(baseColors.accent, 20),
        accentDark: ColorUtils.darken(baseColors.accent, 20)
      },
      
      // Alpha variants
      alpha: {
        primary: this.generateAlphaVariants(baseColors.primary),
        secondary: this.generateAlphaVariants(baseColors.secondary),
        accent: this.generateAlphaVariants(baseColors.accent)
      },
      
      // Text colors - use backend values or generate appropriate contrasting colors
      text: {
        primary: baseColors.onBackground || '#1F2937',
        secondary: '#4B5563',
        muted: '#9CA3AF',
        onPrimary: baseColors.onPrimary || ColorUtils.getContrastingText(baseColors.primary),
        onSecondary: baseColors.onSecondary || ColorUtils.getContrastingText(baseColors.secondary),
        onAccent: ColorUtils.getContrastingText(baseColors.accent),
        onSurface: baseColors.onSurface || '#000000',
        onBackground: baseColors.onBackground || '#000000',
        onSuccess: '#FFFFFF',
        onWarning: '#FFFFFF',
        onError: baseColors.onError || '#FFFFFF',
        onInfo: '#FFFFFF'
      },
      
      // Background colors - use backend values or defaults
      backgrounds: {
        primary: baseColors.background || '#FFFFFF',
        secondary: baseColors.surface || '#F9FAFB',
        tertiary: '#F3F4F6',
        elevated: baseColors.background || '#FFFFFF',
        overlay: 'rgba(0, 0, 0, 0.5)'
      },
      
      // Border colors
      borders: {
        default: '#E5E7EB',
        light: '#F3F4F6',
        dark: '#D1D5DB'
      },
      
      // Shadow colors
      shadows: {
        sm: `0 1px 2px ${ColorUtils.toAlpha(baseColors.primary, 0.05)}`,
        md: `0 4px 6px ${ColorUtils.toAlpha(baseColors.primary, 0.07)}`,
        lg: `0 10px 15px ${ColorUtils.toAlpha(baseColors.primary, 0.10)}`,
        xl: `0 20px 25px ${ColorUtils.toAlpha(baseColors.primary, 0.15)}`
      }
    };
    
    // Add typography if provided
    if (typography) {
      theme.typography = {
        // Font families
        fontFamily: typography.fontFamily,
        headingFont: typography.headingFont,
        bodyFont: typography.bodyFont,
        accentFont: typography.accentFont,
        monoFont: typography.monoFont,
        
        // Font weights
        fontWeights: typography.fontWeights,
        
        // Font sizes
        fontSizes: typography.fontSizes
      };
    }
    
    return theme;
  }
  
  /**
   * Generate alpha variants for a color
   * @param {string} color - Base color
   * @returns {Object} Alpha variants
   */
  generateAlphaVariants(color) {
    const variants = {};
    const alphaValues = [5, 10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90];
    
    alphaValues.forEach(value => {
      variants[value] = ColorUtils.toAlpha(color, value / 100);
    });
    
    return variants;
  }
  
  /**
   * Apply device-specific styles
   * @param {Object} branding - Device branding configuration
   */
  applyDeviceStyles(branding) {
    try {
      const root = document.documentElement;
      
      // Apply additional device branding properties
      if (branding.language) {
        document.documentElement.lang = branding.language;
      }
      
      if (branding.theme) {
        document.body.setAttribute('data-theme', branding.theme);
      }
      
      // Apply device-specific CSS custom properties
      if (branding.backgroundColor) {
        root.style.setProperty('--device-bg-color', branding.backgroundColor);
      }
      
      if (branding.textColor) {
        root.style.setProperty('--device-text-color', branding.textColor);
      }
      
      console.log('Device-specific styles applied');
    } catch (error) {
      console.warn('Failed to apply device styles:', error);
    }
  }
  
  /**
   * Apply theme to DOM (alias for backward compatibility)
   * @param {Object} theme - Theme object
   */
  applyTheme(theme) {
    this.applyThemeToDOM(theme);
  }
  
  /**
   * Apply theme to DOM
   * @param {Object} theme - Theme object
   */
  applyThemeToDOM(theme) {
    const root = document.documentElement;
    
    // Apply base colors
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });
    
    // Apply scales
    Object.entries(theme.scales).forEach(([scaleName, scale]) => {
      Object.entries(scale).forEach(([key, value]) => {
        root.style.setProperty(`--color-${scaleName}-${key}`, value);
      });
    });
    
    // Apply variants
    Object.entries(theme.variants).forEach(([key, value]) => {
      root.style.setProperty(`--color-${this.camelToKebab(key)}`, value);
    });
    
    // Apply alpha variants
    Object.entries(theme.alpha).forEach(([colorName, alphas]) => {
      Object.entries(alphas).forEach(([alpha, value]) => {
        root.style.setProperty(`--color-${colorName}-alpha-${alpha}`, value);
      });
    });
    
    // Apply text colors
    Object.entries(theme.text).forEach(([key, value]) => {
      root.style.setProperty(`--color-text-${this.camelToKebab(key)}`, value);
    });
    
    // Apply backgrounds
    Object.entries(theme.backgrounds).forEach(([key, value]) => {
      root.style.setProperty(`--color-bg-${key}`, value);
    });
    
    // Apply borders
    Object.entries(theme.borders).forEach(([key, value]) => {
      root.style.setProperty(`--color-border-${key}`, value);
    });
    
    // Apply shadows
    Object.entries(theme.shadows).forEach(([key, value]) => {
      root.style.setProperty(`--shadow-${key}`, value);
    });
    
    // Apply typography if available
    if (theme.typography) {
      // Apply font families
      root.style.setProperty('--font-family-primary', theme.typography.fontFamily);
      root.style.setProperty('--font-family-heading', theme.typography.headingFont);
      root.style.setProperty('--font-family-body', theme.typography.bodyFont);
      root.style.setProperty('--font-family-accent', theme.typography.accentFont);
      root.style.setProperty('--font-family-mono', theme.typography.monoFont);
      
      // Apply font weights
      Object.entries(theme.typography.fontWeights).forEach(([key, value]) => {
        root.style.setProperty(`--font-weight-${key}`, value);
      });
      
      // Apply font sizes
      Object.entries(theme.typography.fontSizes).forEach(([key, value]) => {
        root.style.setProperty(`--font-size-${key}`, value);
      });
      
      // Apply fonts to common elements for backward compatibility
      document.body.style.fontFamily = 'var(--font-family-body)';
    }
    
    // Add theme-applied class to body
    document.body.classList.add('theme-applied');
  }
  
  /**
   * Notify theme change listeners
   * @param {Object} theme - New theme
   */
  notifyThemeChange(theme) {
    this.notifyListeners(theme);
  }
  
  /**
   * Convert camelCase to kebab-case
   * @param {string} str - String to convert
   * @returns {string} Kebab-case string
   */
  camelToKebab(str) {
    return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
  }
  
  /**
   * Cache theme for offline use
   * @param {Object} theme - Theme to cache
   */
  cacheTheme(theme) {
    try {
      localStorage.setItem('restaurant_theme', JSON.stringify(theme));
      localStorage.setItem('theme_timestamp', Date.now().toString());
    } catch (error) {
      console.warn('Failed to cache theme:', error);
    }
  }
  
  /**
   * Load cached theme
   * @returns {Object|null} Cached theme or null
   */
  loadCachedTheme() {
    try {
      const cached = localStorage.getItem('restaurant_theme');
      const timestamp = localStorage.getItem('theme_timestamp');
      
      if (!cached) return null;
      
      // Check if cache is older than 24 hours
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      if (timestamp && Date.now() - parseInt(timestamp) > maxAge) {
        this.clearCache();
        return null;
      }
      
      return JSON.parse(cached);
    } catch (error) {
      console.warn('Failed to load cached theme:', error);
      return null;
    }
  }
  
  /**
   * Clear theme cache
   */
  clearCache() {
    try {
      localStorage.removeItem('restaurant_theme');
      localStorage.removeItem('theme_timestamp');
    } catch (error) {
      console.warn('Failed to clear theme cache:', error);
    }
  }
  
  /**
   * Add theme change listener
   * @param {Function} listener - Listener function
   */
  addListener(listener) {
    this.themeChangeListeners.push(listener);
  }
  
  /**
   * Remove theme change listener
   * @param {Function} listener - Listener function
   */
  removeListener(listener) {
    const index = this.themeChangeListeners.indexOf(listener);
    if (index > -1) {
      this.themeChangeListeners.splice(index, 1);
    }
  }
  
  /**
   * Notify all listeners of theme change
   * @param {Object} theme - New theme
   */
  notifyListeners(theme) {
    this.themeChangeListeners.forEach(listener => {
      try {
        listener(theme);
      } catch (error) {
        console.error('Theme listener error:', error);
      }
    });
  }
  
  /**
   * Get current theme
   * @returns {Object} Current theme
   */
  getCurrentTheme() {
    return this.currentTheme;
  }
  
  /**
   * Check contrast for all key combinations
   * @returns {Object} Contrast report
   */
  generateContrastReport() {
    if (!this.currentTheme) return null;
    
    const report = {
      passes: [],
      failures: [],
      warnings: []
    };
    
    const checks = [
      // Text on backgrounds
      { fg: this.currentTheme.text.primary, bg: this.currentTheme.backgrounds.primary, name: 'Primary text on primary bg' },
      { fg: this.currentTheme.text.secondary, bg: this.currentTheme.backgrounds.primary, name: 'Secondary text on primary bg' },
      { fg: this.currentTheme.text.muted, bg: this.currentTheme.backgrounds.primary, name: 'Muted text on primary bg' },
      
      // Text on colored backgrounds
      { fg: this.currentTheme.text.onPrimary, bg: this.currentTheme.colors.primary, name: 'Text on primary color' },
      { fg: this.currentTheme.text.onSecondary, bg: this.currentTheme.colors.secondary, name: 'Text on secondary color' },
      
      // Interactive elements
      { fg: this.currentTheme.colors.primary, bg: this.currentTheme.backgrounds.primary, name: 'Primary button' },
      { fg: this.currentTheme.colors.secondary, bg: this.currentTheme.backgrounds.primary, name: 'Secondary button' }
    ];
    
    checks.forEach(check => {
      const ratio = ColorUtils.getContrastRatio(check.fg, check.bg);
      const meetsAA = ratio >= this.contrastRequirements.AA_normal;
      const meetsAAA = ratio >= this.contrastRequirements.AAA_normal;
      
      const result = {
        name: check.name,
        ratio: ratio.toFixed(2),
        AA: meetsAA,
        AAA: meetsAAA,
        foreground: check.fg,
        background: check.bg
      };
      
      if (meetsAA) {
        report.passes.push(result);
      } else if (ratio >= this.contrastRequirements.AA_large) {
        report.warnings.push({ ...result, note: 'Only passes for large text' });
      } else {
        report.failures.push(result);
      }
    });
    
    return report;
  }
  
  /**
   * Export theme as CSS
   * @returns {string} CSS string
   */
  exportAsCSS() {
    if (!this.currentTheme) return '';
    
    let css = ':root {\n';
    
    // Add all CSS variables
    const styles = document.documentElement.style;
    for (let i = 0; i < styles.length; i++) {
      const prop = styles[i];
      if (prop.startsWith('--color-') || prop.startsWith('--shadow-')) {
        css += `  ${prop}: ${styles.getPropertyValue(prop)};\n`;
      }
    }
    
    css += '}\n';
    
    return css;
  }
}

// Export singleton instance
export const themeManager = new ThemeManager();